import {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import HanziWriter from 'hanzi-writer';

export interface HanziWriterQuizRef {
  restartQuiz: () => void;
}

export type HanziWriterQuizVariant = 'strict' | 'lenient';

/** Cumulative mistakes on try 1 before the word resets from stroke 1. */
const STRICT_STRIKE_LIMIT = 3;

interface HanziWriterQuizProps {
  character: string;
  size?: number;
  /** First remedial try: exact stroke order/quality. Later tries: finish all strokes, much more forgiving. */
  variant?: HanziWriterQuizVariant;
  /** Pause between strokes when animating the full character (Show me). Default 1000. */
  delayBetweenStrokesMs?: number;
  /**
   * Faint outline under the quiz. Stroke matching uses the same geometry either way; turning this off removes
   * the visible guide (harder). Default true.
   */
  showOutline?: boolean;
  onComplete?: (summary: { character: string; totalMistakes: number }) => void;
  onLoadError?: (error?: Error | string) => void;
}

type WriterInstance = ReturnType<typeof HanziWriter.create>;

const STRICT_QUIZ = {
  leniency: 1,
  averageDistanceThreshold: 350,
  acceptBackwardsStrokes: false,
  markStrokeCorrectAfterMisses: false as const,
  /** After one wrong attempt on the current stroke, highlight the correct stroke. */
  showHintAfterMisses: 1 as const,
};

const LENIENT_QUIZ = {
  leniency: 14,
  averageDistanceThreshold: 6000,
  acceptBackwardsStrokes: true,
  markStrokeCorrectAfterMisses: 3 as const,
  showHintAfterMisses: 1 as const,
};

const HanziWriterQuiz = forwardRef<HanziWriterQuizRef, HanziWriterQuizProps>(function HanziWriterQuiz(
  {
    character,
    size = 400,
    variant = 'strict',
    delayBetweenStrokesMs,
    showOutline = true,
    onComplete,
    onLoadError,
  },
  ref
) {
  const boxRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const writerRef = useRef<WriterInstance | null>(null);
  /** Increment when trace box layout size changes so HanziWriter is recreated at the correct pixel size. */
  const [layoutRevision, setLayoutRevision] = useState(0);
  const lastLayoutSideRef = useRef(-1);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const carriedMistakesRef = useRef(0);
  const lastSessionTotalMistakesRef = useRef(0);
  const stuckStrokeIndexRef = useRef(0);

  const [showShowMe, setShowShowMe] = useState(false);
  const [isDemonstrating, setIsDemonstrating] = useState(false);
  const showMeInProgressRef = useRef(false);

  const [strictMistakeCount, setStrictMistakeCount] = useState(0);
  const [strikeResetBanner, setStrikeResetBanner] = useState(false);
  const strikeBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const attachQuiz = useCallback(
    (writer: WriterInstance, quizStartStrokeNum = 0) => {
      const baseOpts = variant === 'strict' ? STRICT_QUIZ : LENIENT_QUIZ;
      writer.quiz({
        ...baseOpts,
        quizStartStrokeNum,
        onMistake: (strokeData) => {
          lastSessionTotalMistakesRef.current = strokeData.totalMistakes;
          stuckStrokeIndexRef.current = strokeData.strokeNum;
          if (variant === 'strict') {
            setStrictMistakeCount(strokeData.totalMistakes);
            if (strokeData.mistakesOnStroke >= 2) {
              setShowShowMe(true);
            }
            if (strokeData.totalMistakes >= STRICT_STRIKE_LIMIT) {
              carriedMistakesRef.current = 0;
              lastSessionTotalMistakesRef.current = 0;
              setShowShowMe(false);
              setStrictMistakeCount(0);
              if (strikeBannerTimerRef.current) clearTimeout(strikeBannerTimerRef.current);
              setStrikeResetBanner(true);
              strikeBannerTimerRef.current = setTimeout(() => {
                setStrikeResetBanner(false);
                strikeBannerTimerRef.current = null;
              }, 2600);
              writer.cancelQuiz();
              attachQuiz(writer, 0);
            }
          }
        },
        onCorrectStroke: () => {
          setShowShowMe(false);
        },
        onComplete: (summary) => {
          setShowShowMe(false);
          if (variant === 'strict') {
            const merged = carriedMistakesRef.current + summary.totalMistakes;
            onCompleteRef.current?.({ ...summary, totalMistakes: merged });
          } else {
            onCompleteRef.current?.(summary);
          }
        },
      });
    },
    [variant]
  );

  const startQuiz = useCallback(() => {
    const container = containerRef.current;
    const box = boxRef.current;
    if (!container || !character) return;
    const bw = box ? Math.floor(box.clientWidth) : 0;
    const side = bw >= 40 ? Math.min(bw, size) : 0;
    if (side < 40) return;

    if (strikeBannerTimerRef.current) {
      clearTimeout(strikeBannerTimerRef.current);
      strikeBannerTimerRef.current = null;
    }
    setStrikeResetBanner(false);

    if (writerRef.current) {
      writerRef.current.cancelQuiz();
      writerRef.current = null;
    }

    container.innerHTML = '';
    carriedMistakesRef.current = 0;
    lastSessionTotalMistakesRef.current = 0;
    showMeInProgressRef.current = false;
    setShowShowMe(false);
    setIsDemonstrating(false);
    setStrictMistakeCount(0);

    const gap = Math.min(8000, Math.max(0, delayBetweenStrokesMs ?? 1000));

    const writer = HanziWriter.create(container, character, {
      width: side,
      height: side,
      padding: 0,
      delayBetweenStrokes: gap,
      showCharacter: false,
      showOutline,
      outlineColor: 'rgba(0,0,0,0.15)',
      strokeColor: '#555',
      drawingColor: '#1f2937',
      drawingWidth: 4,
      strokeWidth: 2,
      outlineWidth: 1,
      highlightColor: '#FBBF24',
      highlightOnComplete: true,
      onLoadCharDataError: (err) => {
        onLoadError?.(err);
      },
    });

    writerRef.current = writer;
    attachQuiz(writer, 0);
  }, [character, size, delayBetweenStrokesMs, showOutline, attachQuiz, onLoadError]);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    lastLayoutSideRef.current = -1;

    const considerBump = () => {
      const w = Math.floor(el.clientWidth);
      if (w < 40) return;
      const nextSide = Math.min(w, size);
      if (Math.abs(nextSide - lastLayoutSideRef.current) <= 2) return;
      lastLayoutSideRef.current = nextSide;
      setLayoutRevision((n) => n + 1);
    };

    considerBump();
    const ro = new ResizeObserver(considerBump);
    ro.observe(el);
    return () => ro.disconnect();
  }, [size]);

  const handleShowMe = useCallback(async () => {
    const writer = writerRef.current;
    if (!writer || variant !== 'strict' || showMeInProgressRef.current) return;

    showMeInProgressRef.current = true;
    carriedMistakesRef.current += lastSessionTotalMistakesRef.current;
    setShowShowMe(false);
    setIsDemonstrating(true);
    try {
      await writer.animateCharacter({});
      attachQuiz(writer, stuckStrokeIndexRef.current);
    } catch {
      attachQuiz(writer, stuckStrokeIndexRef.current);
    } finally {
      setStrictMistakeCount(0);
      showMeInProgressRef.current = false;
      setIsDemonstrating(false);
    }
  }, [variant, attachQuiz]);

  useImperativeHandle(
    ref,
    () => ({
      restartQuiz: () => {
        startQuiz();
      },
    }),
    [startQuiz]
  );

  useEffect(() => {
    startQuiz();
    return () => {
      if (strikeBannerTimerRef.current) clearTimeout(strikeBannerTimerRef.current);
      writerRef.current?.cancelQuiz();
      writerRef.current = null;
    };
  }, [startQuiz, layoutRevision]);

  if (!character) return null;

  return (
    <div className="flex flex-col items-center gap-3 w-full min-w-0 select-none [-webkit-touch-callout:none]">
      <div
        ref={boxRef}
        className="relative mx-auto w-full max-w-[min(100vw-2rem,480px)] aspect-square rounded-xl border-2 border-gray-200 bg-white overflow-hidden touch-none shrink-0 select-none"
      >
        <div ref={containerRef} className="h-full w-full min-h-0 min-w-0 touch-none" />
      </div>

      {variant === 'strict' && (
        <div className="w-full max-w-[min(100vw-2rem,480px)] flex flex-col items-center gap-2 self-stretch">
          {strikeResetBanner && (
            <div
              role="status"
              className="w-full text-center text-xs sm:text-sm font-bold text-amber-950 bg-amber-200/95 border border-amber-400 rounded-xl px-3 py-2.5 shadow-sm"
            >
              3 mistakes — word reset. Start again from stroke 1.
            </div>
          )}
          <div className="w-full rounded-2xl border border-amber-200/90 bg-gradient-to-b from-white via-amber-50/40 to-orange-50/30 px-4 py-3 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-800/85 text-center mb-2">
              Mistakes this round
            </p>
            <div className="flex justify-center gap-2 mb-2">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  title={n <= strictMistakeCount ? `Strike ${n}` : ''}
                  className={`h-2.5 flex-1 max-w-[4.5rem] rounded-full transition-all duration-200 ${
                    strictMistakeCount >= n
                      ? n === STRICT_STRIKE_LIMIT
                        ? 'bg-gradient-to-r from-red-500 to-red-600 shadow-sm'
                        : 'bg-gradient-to-r from-amber-400 to-amber-500'
                      : 'bg-amber-100/90 ring-1 ring-amber-200/80'
                  }`}
                />
              ))}
            </div>
            <p className="text-center text-lg font-black tabular-nums text-amber-950 tracking-tight">
              {strictMistakeCount}
              <span className="text-amber-600/90 font-bold text-base mx-1">/</span>
              {STRICT_STRIKE_LIMIT}
            </p>
            {strictMistakeCount === 2 && (
              <p className="text-center text-[11px] font-semibold text-red-700 mt-1.5 leading-snug">
                Next mistake resets the whole word
              </p>
            )}
            {strictMistakeCount === 0 && !strikeResetBanner && (
              <p className="text-center text-[11px] text-amber-800/75 mt-1 leading-snug">
                One wrong stroke shows a hint; 3 mistakes restart from the first stroke
              </p>
            )}
          </div>
        </div>
      )}

      {variant === 'strict' && showShowMe && !isDemonstrating && (
        <button
          type="button"
          onClick={() => void handleShowMe()}
          className="px-4 py-2 rounded-xl text-sm font-bold bg-amber-100 text-amber-900 border border-amber-300 shadow-sm hover:bg-amber-200 active:scale-[0.98] transition-all touch-manipulation"
        >
          Show me
        </button>
      )}
      {variant === 'strict' && isDemonstrating && (
        <p className="text-xs font-medium text-amber-700">Watch every stroke…</p>
      )}
    </div>
  );
});

export default HanziWriterQuiz;
